import API from '~/services/api'
import { swymFetch } from '~/services/fetch-swym'

import type { IQuestion } from '~/entities/content/question/interfaces/question'
import { _userParser } from '~/entities/user/user/api/parser-users'
import type { IUser } from '~/entities/user/user/interfaces/user'

import { answerParser, commentParser, commentsResponseParser } from '~/features-swym/comment/api/parser-comments'
import type { IAnswer } from '~/features-swym/comment/interfaces/comment'
import type { CommentResponse, IAnswerResponse, IEditAnswerResponse } from '~/features-swym/comment/interfaces/swym-comments'

import type { IParams } from '~/features-shared/content/api/interfaces/params'
import { contentsParser } from '~/features-shared/content/api/parser-contents'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ISwymContent } from '~/features-shared/content/interfaces/swym-content'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

import { mutualizerDecorator } from '~/features-generic/services/utils/decorator-mutualizer'

import type { SwymService } from '~/interfaces/crud-service'
import type { SwymResponse } from '~/interfaces/services-dto'

export enum Maturity {
  Answered = 'answeredbutnotvalidated',
  Validated = 'validated',
  NotAnswered = 'notanswered',
}

export function addQuestionAnswer(question_id: string, answer: string) {
  return swymFetch
    .post('/api/IquestionsAnswer/add', {
      json: {
        params: {
          update: new Date()
            .toISOString(),
          answer,
          qna_question_id: question_id,
        },
      },
    })
    .json<SwymResponse<unknown>>()
    .then((response) => {
      const now = new Date()
        .toISOString()
      const answer: CommentResponse = {
        id: response.result?.answer_id,
        qna_id: response.result?.answer_id,
        subject_uri: response.result?.subject6wuri,
        author: response.result?.author,
        description: response.result?.answer,
        NbRatings: 0,
        moderation_status: 'no_moderation',
        can_validate: response.result?.can_validate,
        my_rate: '0',
        rate: false,
        validate: false,
        can_rate: false,
        has_rated: false,
        created_at: now,
        moderated_at: '0000-00-00 00:00:00',
        published_at: now,
        updated_at: now,
        _acl: { update: { access: true }, delete: { access: true }, forward: { access: false } },
      }

      return { ...response, result: commentParser(answer as unknown as CommentResponse) as IAnswer }
    })
}

export function editQuestionAnswer(question_answer_id: string, answer: string) {
  return swymFetch
    .post('/api/IquestionsAnswer/edit', {
      json: {
        params: {
          update: new Date()
            .toISOString(),
          answer,
          id: question_answer_id,
        },
      },
    })
    .json<SwymResponse<IEditAnswerResponse>>()
    .then((response) => {
      return {
        ...response,
        result: {
          ...answerParser(response.result),
          // FIXME: subjectURI is not return by back-end
          subject_uri: question_answer_id,
        } as IAnswer,
      }
    })
}

export function removeQuestionAnswer(id: string) {
  return swymFetch
    .post('/api/IquestionsAnswer/delete', {
      json: {
        params: {
          id,
        },
      },
    })
    .json()
}

export function validateQuestionAnswer(id: string) {
  return swymFetch
    .post('/api/IquestionsAnswer/validanswer', {
      json: {
        params: {
          answer_id: id,
        },
      },
    })
    .json()
}

export function invalidateQuestionAnswer(id: string) {
  return swymFetch
    .post('/api/answer/invalidate', {
      json: {
        params: {
          answer_id: id,
        },
      },
    })
    .json()
}

export function addMyVoteToAnswer(answer_id: string) {
  return swymFetch
    .post('/api/IquestionsAnswer/addrate', {
      json: {
        params: {
          qna_answer_id: answer_id,
          ratevalue: 1,
        },
      },
    })
    .json()
}

export function removeMyVoteToAnswer(answer_id: string) {
  return swymFetch
    .post('/api/IquestionsAnswer/addrate', {
      json: {
        params: {
          qna_answer_id: answer_id,
          ratevalue: 0,
        },
      },
    })
    .json()
}

interface IQuestionsAnswersOptions {
  subject_uri: string | string[]
  limit?: number
  page?: number
  sortBy?: 'rate' | 'creation_date'
  orderBy?: 'asc' | 'desc'
}

export function _getQuestionsAnswers({
  subject_uri,
  limit = 8,
  page = 1,
  sortBy = 'rate',
  orderBy = 'asc',
}: IQuestionsAnswersOptions) {
  return swymFetch
    .get(`/api/questions/${(Array.isArray(subject_uri) ? subject_uri : [subject_uri]).join(',')}/answers`, {
      searchParams: {
        page_size: limit,
        page_number: page - 1,
        order_by: orderBy,
        sort_by: sortBy,
      },
    })
    .json<SwymResponse<IAnswerResponse>>()
}

export const getQuestionAnswers = mutualizerDecorator<
  typeof _getQuestionsAnswers,
  Parameters<typeof _getQuestionsAnswers>[0]
>({
  fetch: _getQuestionsAnswers,
  merge: (bucketList) => {
    const uris = bucketList.map(el => el.subject_uri as string)
    return {
      ...bucketList.pop(), // useful to keep the limit
      subject_uri: uris,
    }
  },
  resolve: ([response, [option, resolve]]) => {
    resolve(commentsResponseParser({
      ...response,
      result: {
        comments: response.result[option.subject_uri]?.answers || [],
        isValidated: response.result[option.subject_uri]?.is_validated,
      },
      nb_result: response.result[option.subject_uri]?.nb_total_answers || 0,
    }) as unknown as SwymResponse<IAnswerResponse>)
  },

})

// TODO: In case, we want swym api for questions
export const QuestionsService: SwymService<IQuestion> = {
  async count(/* params */) {
    throw new Error('Function not implemented.')
  },
  async getAll(params) {
    const response = await swymFetch
      .post(API.Question.Swym.Content, {
        json: {
          params: {
            content_type: IContentModel.Question,
            ...params,
          } as IParams,
        },
      })
      .json<SwymResponse<ISwymContent[]>>()

    return {
      total: Number.parseInt(response.nb_result || '0') || 0,
      data: contentsParser(response).result as IQuestion[],
    }
  },
}

/**
 * Function that returns a list of users that answered a question
 *
 * @param options Options to get the users
 * @param options.subject_uri The subject URI of the answer
 * @param options.page The page number to fetch. The first page is 0
 * @param options.limit maximum number of results per page
 *
 * @returns List of users
 */
export function getUsersWhoAnswered(options: { subject_uri: SUBJECT_6W_URI, page: number, limit: number }) {
  return swymFetch
    .post('/api/iquestionsAnswer/getwhoanswered', {
      json: {
        params: {
          qna_id: options.subject_uri,
          limit: options.limit,
          page_number: options.page,
        },
      },
    })
    .json<SwymResponse<IUser[]>>()
    .then((response) => {
      response.result = response.result.map(_userParser)
      return response as SwymResponse<IUser[]>
    })
}

/**
 * Function that returns a list of users that rated (endorsed) an answer
 *
 * @param options Options to get the users
 * @param options.subject_uri The subject URI of the answer
 * @param options.page The page number to fetch. The first page is 0
 * @param options.limit maximum number of results per page
 *
 * @returns List of users
 */
export function getUsersWhoRated(options: { subject_uri: SUBJECT_6W_URI, page: number, limit: number }) {
  return swymFetch
    .post('/api/iquestionsAnswer/getwhorated', {
      json: {
        params: {
          answer_id: options.subject_uri,
          limit: options.limit,
          page_number: options.page,
        },
      },
    })
    .json<SwymResponse<IUser[]>>()
    .then((response) => {
      response.result = response.result.map(_userParser)
      return response as SwymResponse<IUser[]>
    })
}
